import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Package, Pencil, Trash2, FileText, Loader2 } from "lucide-react";
import { resolveImageUrl } from "@/utils/imageUrl";
import { ViewOnlyBanner } from "./ViewOnlyBanner";

export const ProductsTab = ({
  darkMode, products, loading, adminSearch,
  openEditProduct, handleDeleteProduct,
  setEditingProduct, setProductForm, setProductDialogOpen,
  initialProductForm, formatPrice, viewOnly
}) => {
  const cardBg = darkMode ? "bg-gray-800/80 border border-gray-700/50" : "";

  return (
    <>
    {viewOnly && <ViewOnlyBanner darkMode={darkMode} />}
    <Card className={`border-0 shadow-soft rounded-3xl ${cardBg}`}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className={darkMode ? "text-gray-100" : ""}>Products</CardTitle>
        {!viewOnly && (
        <Button onClick={() => { setEditingProduct(null); setProductForm(initialProductForm); setProductDialogOpen(true); }} className="rounded-xl bg-primary" data-testid="add-product-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Product
        </Button>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className={`w-8 h-8 animate-spin ${darkMode ? "text-blue-400" : "text-primary"}`} /></div>
        ) : products.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className={darkMode ? "border-gray-700" : ""}>
                  <TableHead className={darkMode ? "text-gray-400" : ""}>Image</TableHead>
                  <TableHead className={darkMode ? "text-gray-400" : ""}>Title</TableHead>
                  <TableHead className={darkMode ? "text-gray-400" : ""}>Subject</TableHead>
                  <TableHead className={darkMode ? "text-gray-400" : ""}>Class</TableHead>
                  <TableHead className={darkMode ? "text-gray-400" : ""}>Price</TableHead>
                  <TableHead className={darkMode ? "text-gray-400" : ""}>File</TableHead>
                  <TableHead className={darkMode ? "text-gray-400" : ""}>Status</TableHead>
                  <TableHead className={`text-right ${darkMode ? "text-gray-400" : ""}`}>{!viewOnly && "Actions"}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.filter(p => !adminSearch || p.title?.toLowerCase().includes(adminSearch.toLowerCase()) || p.subject?.toLowerCase().includes(adminSearch.toLowerCase()) || p.class_level?.toLowerCase().includes(adminSearch.toLowerCase())).slice(0, 20).map(product => (
                  <TableRow key={product.id} className={darkMode ? "border-gray-700/50 hover:bg-gray-700/30" : ""}>
                    <TableCell>
                      {product.cover_image_url ? (
                        <img src={resolveImageUrl(product.cover_image_url)} alt="" className="w-10 h-10 rounded-lg object-cover" />
                      ) : (
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${darkMode ? "bg-gray-700" : "bg-gray-100"}`}>
                          <Package className="w-4 h-4 text-gray-400" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className={`font-medium max-w-[200px] truncate ${darkMode ? "text-gray-200" : ""}`}>{product.title}</TableCell>
                    <TableCell className={darkMode ? "text-gray-300" : ""}>{product.subject}</TableCell>
                    <TableCell className={darkMode ? "text-gray-300" : ""}>{product.class_level}</TableCell>
                    <TableCell className={darkMode ? "text-gray-200" : ""}>{formatPrice(product.price_ghs)}</TableCell>
                    <TableCell>
                      {product.file_path ? (
                        <FileText className="w-4 h-4 text-green-500" title={product.file_path} />
                      ) : (
                        <FileText className={`w-4 h-4 ${darkMode ? "text-gray-600" : "text-gray-300"}`} title="No file uploaded" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={product.is_active ? "default" : "secondary"} className="rounded-full">
                        {product.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {!viewOnly && (
                      <div className="flex justify-end gap-1">
                        <Button variant="outline" size="icon" className={`rounded-xl ${darkMode ? "bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600 hover:text-white" : ""}`} onClick={() => openEditProduct(product)} data-testid={`edit-product-${product.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="icon" className={`rounded-xl ${darkMode ? "text-red-400 hover:text-red-300 border-gray-600 hover:bg-red-900/20" : "text-destructive hover:text-destructive hover:bg-red-50"}`} onClick={() => handleDeleteProduct(product.id)} data-testid={`delete-product-${product.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-12">
            <Package className={`w-16 h-16 mx-auto mb-4 ${darkMode ? "text-gray-600" : "text-muted-foreground"}`} />
            <p className={darkMode ? "text-gray-400" : "text-muted-foreground"}>No products yet</p>
          </div>
        )}
      </CardContent>
    </Card>
    </>
  );
};
